import React, { FC } from 'react'
import { useQueryClient } from 'react-query'
import { AppButton } from 'components/Common/AppButton/AppButton.component'
import { AppModalBox } from 'components/Common/AppModalBox/AppModalBox.component'
import { AppBinWarningIcon } from 'components/Common/AppSvgIcons/AppBinWarningIcon'
import { AppTaskCard } from '../AppTaskCard/AppTaskCard.component'
import {
  UserTask,
  UserTaskItemStatus,
  useUpdateUserTaskMutation,
} from '__generated__/api-types-and-hooks'
import { getTenantId } from 'utils/getTenantId'

interface IAppDeleteTask {
  task: UserTask
  userId: string
  userAvatar: string
  userFirstName: string
  userLastName: string
  onCancel: () => void
  onDeleteClose: () => void
}

export const AppDeleteTask: FC<IAppDeleteTask> = ({
  task,
  userId,
  onCancel,
  userAvatar,
  userLastName,
  userFirstName,
  onDeleteClose,
}) => {
  const queryClient = useQueryClient()
  const tenantId = getTenantId()
  const {
    mutate: updateUserTask,
    isLoading: isUpdateLoading,
    isSuccess: isUpdateSuccess,
  } = useUpdateUserTaskMutation()
  return (
    <div>
      <AppModalBox
        classes="p-6 pt-0 z-50"
        height="h-auto"
        width="w-[400px] "
        onClose={() => {
          if (!isUpdateLoading) {
            onCancel()
            if (isUpdateSuccess) onDeleteClose()
          }
        }}
      >
        <div className="flex flex-col items-center ">
          <div className="bg-app-red-25 rounded-xl p-2">
            <AppBinWarningIcon />
          </div>

          <h2 className=" font-inter text-xl font-medium text-app-black-100 mt-4">{`${
            isUpdateSuccess
              ? 'Deleted Play'
              : task.status !== UserTaskItemStatus.Done
              ? 'Delete Play?'
              : ''
          }`}</h2>
          {!isUpdateSuccess && (
            <p className="text-app-grey-70 text-sm text-center mt-2 font-inter">
              {task.status === UserTaskItemStatus.Done ? (
                <>You’ve already completed this play.</>
              ) : (
                'Are you sure you want to delete this play? The action is irreversible.'
              )}
            </p>
          )}
        </div>
        <div className="mt-8 rounded-lg  border border-app-grey-20 shadow-md p-4 text-black min-h-[100px] bg-white cursor-pointer flex justify-between flex-col">
          <AppTaskCard
            task={task}
            userId={userId}
            userAvatar={userAvatar}
            userFirstName={userFirstName}
            userLastName={userLastName}
          />
        </div>

        <div className="flex flex-col w-full gap-2 mt-8">
          {!isUpdateSuccess && (
            <AppButton
              label="Delete Play"
              variant="alert"
              size="md"
              onClick={() => {
                console.log('Delete button clicked for task:', task.taskId, {
                  tenantId,
                  userId: task.userId ?? userId,
                  taskId: task.taskId,
                })
                updateUserTask(
                  {
                    input: {
                      tenantId,
                      userId: task.userId ?? userId,
                      taskId: task.taskId!,
                      status: UserTaskItemStatus.Deleted,
                    },
                  },
                  {
                    onSuccess: async () => {
                      // Invalidate all relevant queries to ensure fresh data
                      // Use both string and array formats to catch all variations
                      await Promise.all([
                        queryClient.invalidateQueries('getUserTasks'),
                        queryClient.invalidateQueries('getAdvisorAssignedTasks'),
                        queryClient.invalidateQueries('getBusinessGoal'),
                        queryClient.invalidateQueries('getUserTask'),
                        queryClient.invalidateQueries('getAdvisorDashboard'),
                        queryClient.invalidateQueries('searchUserTasks'),
                        queryClient.invalidateQueries({ queryKey: ['getUserTasks'] }),
                        queryClient.invalidateQueries({ queryKey: ['getAdvisorAssignedTasks'] }),
                        queryClient.invalidateQueries({ queryKey: ['getBusinessGoal'] }),
                        queryClient.invalidateQueries({ queryKey: ['getUserTask'] }),
                        queryClient.invalidateQueries({ queryKey: ['getAdvisorDashboard'] }),
                        queryClient.invalidateQueries({ queryKey: ['searchUserTasks'] }),
                      ])
                      // Invalidate all queries that might contain tasks (broader invalidation)
                      queryClient.invalidateQueries({
                        predicate: (query) => {
                          const queryKey = query.queryKey?.[0] as string
                          return (
                            typeof queryKey === 'string' &&
                            (queryKey.includes('Task') ||
                              queryKey.includes('task') ||
                              queryKey.includes('Advisor') ||
                              queryKey.includes('advisor'))
                          )
                        },
                      })
                      // Call onDeleteClose after queries are invalidated
                      onDeleteClose()
                    },
                    onError: (error) => {
                      console.error('Error deleting task:', error)
                    },
                  }
                )
              }}
              disabled={isUpdateLoading || task.status === UserTaskItemStatus.Done}
            />
          )}
        </div>
      </AppModalBox>
    </div>
  )
}
